<template>
  <DialogLayout :class-name="'job-application-dialog'" @close="handleClose">
    <template #title>{{ t('candidate.applyJob') }}</template>
    <template #default>
      <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'left'" class="application-form">
        <el-form-item v-if="!isBiz" :label="t('employee.filters.enterpriseName')" prop="enterpriseId">
          <el-select v-model="formData.enterpriseId" :placeholder="t('common.pleaseSelect')" filterable @change="loadJobs">
            <el-option v-for="enterprise in enterpriseOptions" :key="enterprise.id" :label="enterprise.name"
              :value="enterprise.id">
            </el-option>
          </el-select>
        </el-form-item>
          <el-form-item :label="t('candidate.job')" prop="jobId">
            <el-select v-model="formData.jobId" :placeholder="t('common.pleaseSelect')" filterable :loading="jobsLoading"
              popper-class="job-application-select-dropdown">
            <el-option v-for="job in jobOptions" :key="job.jobId" :label="`${job.jobId} | ${job.title}`"
              :value="job.jobId">
              <template #default>
                <div class="job-option">
                  <div class="job-option-header">

                    <div v-hover-tip.over="job.jobId + ' | ' + job.title" class="job-name">{{job.jobId}} | {{ job.title
                      }}</div>
                  </div>
                  <div class="job-option-details">
                    <div v-hover-tip.over="job.affiliationName" class="job-option-details-item">{{ job.affiliationName
                      || '-' }}</div>
                    <span class="separator" style="margin: 0 4px; color: #C9D3D9;">|</span>
                    <span v-hover-tip.over="job.positionTypeName" class="job-option-details-item"
                      style="max-width: 260px;">{{ job.positionTypeName
                      || '-' }}</span>
                  </div>
                </div>
              </template>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="t('candidate.recommendationReason')" prop="reason">
          <el-input v-model="formData.reason" type="textarea" :placeholder="t('common.pleaseEnter')" :rows="4"
            :maxlength="3000" show-word-limit />
        </el-form-item>
      </el-form>
    </template>
    <template #footer>
      <el-button class="confirm-button-default" @click="handleCancel">
        {{ t('candidate.cancel') }}
      </el-button>
      <el-button type="primary" :loading="submitting" @click="handleConfirm">
        {{ t('candidate.apply') }}
      </el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue'
import { getCandidateJobsApi, getHeadhuntingJobCandidateJobsApi, getHeadhuntingEnterpriseListApi } from '@/api/candidate'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

interface JobOption {
    jobId: number
    title: string
    jobType: number
    affiliationName: string
    employmentFormStr: string
}

interface EnterpriseOption {
  id: number
  name: string
}
interface Props {
  candidateId?: number
  candidateName?: string
}
import { useUserStore } from '@/store/modules/user';
import { System } from '@/router/config/roles';
const userStore = useUserStore();
// 判断是否是猎头端
const isBiz = computed(() => {
  console.log('userStore.userSystem', userStore.userSystem)
  if (userStore.userSystem == System.business) {
    return true
  }
  return false
})

const props = defineProps<Props>()

const emit = defineEmits<{
  cancel: []
  confirm: [data: { jobId: number; reason: string }]
  close: []
}>()

const enterpriseOptions = ref<EnterpriseOption[]>([])
const formRef = ref<FormInstance>()
const submitting = ref(false)
const jobsLoading = ref(false)
const jobOptions = ref<JobOption[]>([])

const formData = reactive({
  jobId: undefined as number | undefined,
  reason: ''
})

const rules = computed<FormRules>(() => ({
  enterpriseId: [
    { required: true, message: t('candidate.pleaseSelectEnterprise'), trigger: 'blur' }
  ],
  jobId: [
    { required: true, message: t('candidate.pleaseSelectJob'), trigger: 'blur' }
  ],
  reason: [
    { required: true, message: t('candidate.pleaseEnterReason'), trigger: 'blur' }
  ]
}))

// 加载职位列表
const loadJobs = async () => {
  jobsLoading.value = true
  try {
    const response = isBiz.value ? await getCandidateJobsApi(props.candidateId) : await getHeadhuntingJobCandidateJobsApi(props.candidateId, formData.enterpriseId)
    if (response) {
      jobOptions.value = response || []
    } 
  } catch (error) {
    console.error('加载职位列表失败:', error)
    jobOptions.value = []
  } finally {
    jobsLoading.value = false
  }
}

// 组件挂载时加载职位列表
onMounted(() => {
  if (!isBiz.value) {
    getHeadhuntingEnterpriseListApi().then(res => {
      enterpriseOptions.value = res || []
    })
  } else {
    loadJobs()
  }
})

const handleCancel = () => {
  emit('cancel')
}

const handleConfirm = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
    submitting.value = true

    emit('confirm', {
      jobId: formData.jobId!,
      reason: formData.reason
    })
  } catch (error) {
    console.error('表单验证失败:', error)
  } finally {
    submitting.value = false
  }
}

const handleClose = () => {
  emit('close')
}
</script>

<style lang="scss" scoped>
.job-application-dialog {
  min-width: 560px;
  background: #ffffff;
}

.application-form {
  :deep(.el-form-item) {
    margin-bottom: 20px;

    .el-form-item__label {
      font-size: 14px;
    font-weight: 500;
      color: #212429;
      line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;
        min-width: 100px;

      &::before {
        margin-right: 2px;
      }
    }

    .el-form-item__content {
      line-height: normal;
    }
  }

  :deep(.el-select) {
    width: 100%;
  }

  :deep(.el-textarea__inner) {
    font-family: inherit;
  }
}


:deep(.job-option) {
  display: flex;
  flex-direction: column;
  width: 100%;

  .job-option-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;

    .job-name {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
    //  color: #212429;
      max-width: 320px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .skill-tag {
      border: none !important;
      border-radius: 463px;
      font-size: 12px;
      padding: 2px 8px;
      font-weight: 500;
    }

    .skill-tag1 {
      background-color: #DBF1F1 !important;
      color: #009E99 !important;
    }
        .skill-tag2 {
            background-color: #EDEFFD !important;
            color: #1F69FF !important;

        }
                .skill-tag3 {
                    background-color: #FAECD8 !important;
                    color: #B88230 !important;
                }
  }

  .job-option-details {
    font-size: 12px !important;
    color: #86909C !important;
    display: flex !important;
    align-items: center !important;
    gap: 4px !important;
    margin-top: 4px !important;

    .job-option-details-item {
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .separator {
      color: #E5E6EB !important;
      margin: 0 4px !important;
    }
  }
}
</style>

<!-- 非 scoped 样式，用于下拉菜单（挂载在 body 上） -->
<style lang="scss">
// 选中项中的 span 样式 - 只作用于当前组件的下拉菜单
.job-application-select-dropdown {
  .el-select-dropdown__item {
      height: 55px !important;
      padding: 6px 12px !important;
      line-height: normal !important;
      margin: 0 10px;
      border-radius: 8px;
  
      &:hover {
        background-color: #F4EFFD !important;
      }
  
      &.selected {
        background-color: #F4EFFD !important;
      }
  
      &.active {
        background-color: #F4EFFD !important;
      }
  
      &.focus {
        background-color: #F4EFFD !important;
      }
    }
  
    .el-select-dropdown__item.is-hovering {
      background-color: #F4EFFD !important;
    }
    .el-select-dropdown__list {
        .el-select-dropdown__item {
            &.is-selected {
                font-weight: 500 !important;
                span {
                    padding-right: 0px !important;
                }
            }
        }
    }
}
</style>

